/**
 * BLRRD Pointwise Filters — Channel Mixer
 *
 * User-tunable 3×3 colour matrix with optional per-channel offset.
 * Each output channel is a weighted sum of input R/G/B plus a
 * constant — same formula every DCC tool ships:
 *
 *     R' = mRR * R + mRG * G + mRB * B + offsetR
 *     G' = mGR * R + mGG * G + mGB * B + offsetG
 *     B' = mBR * R + mBG * G + mBB * B + offsetB
 *
 * Pipeline slot: PRE-KERNEL scene-linear ACEScg. The matrix
 * operates on linear light — matches the way colorists tune
 * channel mixers in Resolve / Baselight (scene-referred, linear).
 *
 * Common uses:
 *   - Mono conversion: R=0.2126 G=0.7152 B=0.0722 (Rec.709 luma)
 *   - Red-channel reassignment for film simulation
 *   - Tinted black-and-white via row sums
 *
 * Default: 3×3 identity matrix + zero offsets → identity short-
 * circuit. Schlag 1994 ("Color Spaces") + Adobe Photoshop's
 * Channel Mixer dialog (public since v5.5, 1999) for citation.
 */

const IDENTITY_EPS = 1e-7;

/* Float32Array views can't be frozen on V8 — return a fresh copy from
 * `defaultChannelMixerParams()` so callers can't accidentally mutate
 * this module-level reference. */
const IDENTITY_MATRIX = new Float32Array([
    1, 0, 0,
    0, 1, 0,
    0, 0, 1,
]);

/** Default Channel Mixer params (identity). */
export function defaultChannelMixerParams() {
    return {
        matrix: new Float32Array(IDENTITY_MATRIX),
        offsets: [0, 0, 0],
    };
}

/** True iff `params` represents the identity transform. */
export function isIdentityChannelMixer(params) {
    if (!params) return true;
    const m = params.matrix;
    if (!m || m.length !== 9) return true;
    for (let i = 0; i < 9; i++) {
        if (Math.abs(m[i] - IDENTITY_MATRIX[i]) > IDENTITY_EPS) return false;
    }
    const o = params.offsets;
    if (o) {
        if (Math.abs(o[0] || 0) > IDENTITY_EPS) return false;
        if (Math.abs(o[1] || 0) > IDENTITY_EPS) return false;
        if (Math.abs(o[2] || 0) > IDENTITY_EPS) return false;
    }
    return true;
}

/**
 * Apply Channel Mixer to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { matrix: Float32Array(9), offsets: [r,g,b] }
 * @param {object}       [opts]      { selectionMask? }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applyChannelMixer(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentityChannelMixer(params)) return rgba;
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyChannelMixer: selectionMask length mismatch');
    }

    const M = params.matrix;
    const oR = (params.offsets && params.offsets[0]) || 0;
    const oG = (params.offsets && params.offsets[1]) || 0;
    const oB = (params.offsets && params.offsets[2]) || 0;
    const N = w * h;

    for (let p = 0; p < N; p++) {
        const i = p << 2;
        const m = mask ? mask[p] : 1;
        if (m <= 0) continue;
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        const newR = M[0] * r + M[1] * g + M[2] * b + oR;
        const newG = M[3] * r + M[4] * g + M[5] * b + oG;
        const newB = M[6] * r + M[7] * g + M[8] * b + oB;
        if (m >= 1) {
            rgba[i]     = newR;
            rgba[i + 1] = newG;
            rgba[i + 2] = newB;
        } else {
            const invM = 1 - m;
            rgba[i]     = invM * r + m * newR;
            rgba[i + 1] = invM * g + m * newG;
            rgba[i + 2] = invM * b + m * newB;
        }
    }
    return rgba;
}

/** Identity matrix for callers building presets. */
export { IDENTITY_MATRIX };

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applyChannelMixer: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyChannelMixer: rgba.length !== w*h*4');
    }
}
